import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const stepIconMap = {
  basic: <PersonOutlineRoundedIcon fontSize="small" />,
  preferences: <TuneRoundedIcon fontSize="small" />,
  permissions: <ShieldRoundedIcon fontSize="small" />,
};

const labelWithRequired = (label, required) => (
  <span>
    {label}
    {required ? <span style={{ color: "#dc2626" }}> *</span> : null}
  </span>
);

const sectionSx = {
  borderRadius: 4,
  border: "1px solid #dbe4f0",
  backgroundColor: "#ffffff",
  boxShadow: "0 20px 40px rgba(15, 23, 42, 0.05)",
};

const renderField = (field, form, onChange, errors, passwordVisibility, setPasswordVisibility) => {
  const value = field.path ? field.path.split(".").reduce((acc, key) => acc?.[key], form) ?? "" : "";
  const error = errors?.[field.path];
  const isPassword = field.type === "password";
  const isSelect = Array.isArray(field.options);
  const nextType = isPassword ? (passwordVisibility[field.path] ? "text" : "password") : field.type || "text";

  return (
    <Grid item xs={12} md={field.md || 6} key={field.path || field.label}>
      {field.render ? (
        field.render()
      ) : (
        <TextField
          fullWidth
          label={labelWithRequired(field.label, field.required)}
          placeholder={field.placeholder}
          type={nextType}
          select={isSelect}
          value={value}
          onChange={(event) => {
            const rawValue = field.type === "checkbox" ? event.target.checked : event.target.value;
            onChange(field.path, field.transform ? field.transform(rawValue) : rawValue);
          }}
          error={Boolean(error)}
          helperText={error || field.helperText || " "}
          required={field.required}
          size="medium"
          InputLabelProps={field.inputLabelProps}
          inputProps={field.inputProps}
          InputProps={{
            ...field.InputProps,
            startAdornment: field.startAdornment ? (
              <InputAdornment position="start">{field.startAdornment}</InputAdornment>
            ) : field.InputProps?.startAdornment,
            endAdornment: isPassword ? (
              <InputAdornment position="end">
                <IconButton
                  edge="end"
                  onClick={() =>
                    setPasswordVisibility((prev) => ({
                      ...prev,
                      [field.path]: !prev[field.path],
                    }))
                  }
                >
                  {passwordVisibility[field.path] ? (
                    <VisibilityOffOutlinedIcon fontSize="small" />
                  ) : (
                    <VisibilityOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              </InputAdornment>
            ) : (
              field.InputProps?.endAdornment
            ),
          }}
          multiline={field.multiline}
          minRows={field.minRows}
        >
          {isSelect
            ? field.options.map((option) => {
                const optionValue = typeof option === "string" ? option : option.value;
                const optionLabel = typeof option === "string" ? option : option.label;
                return (
                  <MenuItem key={optionValue} value={optionValue}>
                    {optionLabel}
                  </MenuItem>
                );
              })
            : null}
        </TextField>
      )}
    </Grid>
  );
};

export function StepSidebar({ steps, currentStep, onStepChange, helpTitle, helpText }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  if (isMobile) {
    return (
      <Box sx={{ px: 2, pt: 2, pb: 1, borderBottom: "1px solid #e2e8f0", backgroundColor: "#fff" }}>
        <Stack direction="row" spacing={1} sx={{ overflowX: "auto", pb: 0.5 }}>
          {steps.map((step, index) => {
            const active = index === currentStep;
            const completed = index < currentStep;
            return (
              <Button
                key={step.title}
                variant={active ? "contained" : "outlined"}
                onClick={() => onStepChange(index)}
                sx={{
                  flexShrink: 0,
                  borderRadius: 999,
                  px: 1.75,
                  py: 0.9,
                  minWidth: "auto",
                  color: active ? "#fff" : "#1e3a8a",
                  borderColor: "#c7d2fe",
                  background: active ? "linear-gradient(135deg, #2563eb, #1d4ed8)" : "#f8fbff",
                  boxShadow: active ? "0 12px 24px rgba(37, 99, 235, 0.24)" : "none",
                }}
              >
                <Stack alignItems="flex-start" spacing={0.35}>
                  <Typography sx={{ fontSize: 11, fontWeight: 800 }}>
                    {completed ? "Done" : `Step ${index + 1}`}
                  </Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, whiteSpace: "nowrap" }}>
                    {step.title}
                  </Typography>
                </Stack>
              </Button>
            );
          })}
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: 286,
        borderRight: "1px solid #e2e8f0",
        background:
          "linear-gradient(180deg, rgba(248,250,252,0.96) 0%, rgba(239,246,255,0.98) 100%)",
        p: 2.25,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Stack spacing={1.25}>
        {steps.map((step, index) => {
          const active = index === currentStep;
          const completed = index < currentStep;
          return (
            <Button
              key={step.title}
              onClick={() => onStepChange(index)}
              sx={{
                justifyContent: "flex-start",
                p: 1.25,
                borderRadius: 3,
                textTransform: "none",
                color: active ? "#1d4ed8" : "#334155",
                backgroundColor: active ? "#ffffff" : "transparent",
                border: active ? "1px solid #bfdbfe" : "1px solid transparent",
                boxShadow: active ? "0 12px 28px rgba(37, 99, 235, 0.08)" : "none",
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    bgcolor: completed ? "#16a34a" : active ? "#2563eb" : "#cbd5e1",
                    color: "#fff",
                    fontWeight: 800,
                    flexShrink: 0,
                  }}
                >
                  {completed ? <CheckCircleRoundedIcon sx={{ fontSize: 18 }} /> : index + 1}
                </Box>
                <Box sx={{ textAlign: "left" }}>
                  <Typography sx={{ fontSize: 15, fontWeight: 800 }}>{step.title}</Typography>
                  <Typography sx={{ fontSize: 12.5, color: "#64748b" }}>{step.description}</Typography>
                </Box>
              </Stack>
            </Button>
          );
        })}
      </Stack>

      {(helpTitle || helpText) && (
        <Paper
          variant="outlined"
          sx={{
            mt: "auto",
            p: 2,
            borderRadius: 3,
            borderColor: "#bfdbfe",
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(239,246,255,0.9) 100%)",
          }}
        >
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <InfoOutlinedIcon sx={{ color: "#2563eb", fontSize: 18 }} />
              <Typography sx={{ fontWeight: 800, color: "#2563eb" }}>{helpTitle}</Typography>
            </Stack>
            <Typography sx={{ fontSize: 13.5, lineHeight: 1.65, color: "#475569" }}>
              {helpText}
            </Typography>
          </Stack>
        </Paper>
      )}
    </Box>
  );
}

export function WizardModalLayout({
  open,
  onClose,
  icon,
  title,
  subtitle,
  steps,
  currentStep,
  onStepChange,
  children,
  footer,
  error,
  helpTitle,
  helpText,
  fullScreen = false,
  maxWidth = "lg",
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const ActiveIcon = icon || stepIconMap[steps[currentStep]?.key] || PersonOutlineRoundedIcon;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={maxWidth}
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 4,
          overflow: "hidden",
          boxShadow: "0 32px 80px rgba(15, 23, 42, 0.24)",
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)",
          height: fullScreen ? "100dvh" : "min(92vh, 920px)",
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2.25,
            borderBottom: "1px solid #e2e8f0",
            background:
              "radial-gradient(circle at top left, rgba(219,234,254,0.95), rgba(255,255,255,0.98) 34%), #fff",
          }}
        >
          <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between">
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: 3,
                  display: "grid",
                  placeItems: "center",
                  background: "linear-gradient(135deg, #eff6ff, #dbeafe)",
                  color: "#2563eb",
                  border: "1px solid #bfdbfe",
                  flexShrink: 0,
                }}
              >
                {React.isValidElement(ActiveIcon) ? ActiveIcon : <ActiveIcon />}
              </Box>
              <Box>
                <Typography sx={{ fontSize: { xs: 24, sm: 34 }, fontWeight: 900, color: "#0f172a" }}>
                  {title}
                </Typography>
                <Typography sx={{ mt: 0.45, color: "#64748b", fontSize: 14.5 }}>
                  {subtitle}
                </Typography>
              </Box>
            </Stack>

            <IconButton onClick={onClose} sx={{ color: "#475569" }}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>

        <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
          <StepSidebar
            steps={steps}
            currentStep={currentStep}
            onStepChange={onStepChange}
            helpTitle={helpTitle}
            helpText={helpText}
          />

          <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0, minHeight: 0 }}>
            <DialogContent
              sx={{
                p: { xs: 2, sm: 3 },
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                background:
                  isMobile
                    ? "#f8fafc"
                    : "linear-gradient(180deg, rgba(248,250,252,0.96) 0%, rgba(255,255,255,0.98) 100%)",
              }}
            >
              <Stack spacing={2.25}>
                <Stack spacing={1}>
                  <Chip
                    label={`Step ${currentStep + 1} of ${steps.length}`}
                    sx={{
                      alignSelf: "flex-start",
                      fontWeight: 800,
                      backgroundColor: "#e0ecff",
                      color: "#2563eb",
                    }}
                  />
                  <Box>
                    <Typography sx={{ fontSize: { xs: 26, sm: 30 }, fontWeight: 900, color: "#0f172a" }}>
                      {steps[currentStep]?.title}
                    </Typography>
                    <Typography sx={{ mt: 0.5, color: "#64748b", fontSize: 14.5 }}>
                      {steps[currentStep]?.description}
                    </Typography>
                  </Box>
                </Stack>

                {error ? (
                  <Alert severity="error" sx={{ borderRadius: 3 }}>
                    {error}
                  </Alert>
                ) : null}

                {children}
              </Stack>
            </DialogContent>
            {footer}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}

export function StickyModalFooter({
  currentStep,
  steps,
  loading,
  onBack,
  onCancel,
  onNext,
  onSubmit,
  submitLabel,
  submitLoadingLabel = "Saving...",
  nextLabel,
  disableNext = false,
  disableSubmit = false,
}) {
  const isLast = currentStep === steps.length - 1;

  return (
    <Box
      sx={{
        position: "sticky",
        bottom: 0,
        zIndex: 2,
        p: 2,
        borderTop: "1px solid #e2e8f0",
        background: "rgba(255,255,255,0.94)",
        backdropFilter: "blur(16px)",
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
        <Button
          variant="outlined"
          onClick={currentStep === 0 ? onCancel : onBack}
          startIcon={currentStep === 0 ? null : <ArrowBackRoundedIcon />}
          sx={{
            borderRadius: 3,
            px: 2.25,
            py: 1.15,
            fontWeight: 800,
            minWidth: 130,
            borderColor: "#cbd5e1",
            color: "#334155",
          }}
        >
          {currentStep === 0 ? "Cancel" : "Back"}
        </Button>

        <Button
          variant="contained"
          onClick={isLast ? onSubmit : onNext}
          disabled={loading || (isLast ? disableSubmit : disableNext)}
          endIcon={isLast ? null : <ArrowForwardRoundedIcon />}
          sx={{
            borderRadius: 3,
            px: 2.5,
            py: 1.15,
            minWidth: 150,
            fontWeight: 800,
            background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
            boxShadow: "0 18px 34px rgba(37, 99, 235, 0.28)",
          }}
        >
          {loading
            ? submitLoadingLabel
            : isLast
              ? submitLabel
              : nextLabel || `Next: ${steps[currentStep + 1]?.title || "Continue"}`}
        </Button>
      </Stack>
    </Box>
  );
}

export function BasicDetailsStep({
  title = "Account Information",
  description,
  form,
  onChange,
  fields,
  errors,
  tip,
}) {
  const [passwordVisibility, setPasswordVisibility] = useState({});

  return (
    <Stack spacing={2}>
      <Paper sx={{ ...sectionSx, p: { xs: 2, sm: 2.5 } }}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                bgcolor: "#e0ecff",
                color: "#2563eb",
              }}
            >
              <PersonOutlineRoundedIcon fontSize="small" />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 18, fontWeight: 800 }}>{title}</Typography>
              {description ? (
                <Typography sx={{ fontSize: 13.5, color: "#64748b" }}>{description}</Typography>
              ) : null}
            </Box>
          </Stack>

          <Grid container spacing={2}>
            {fields.map((field) =>
              renderField(field, form, onChange, errors, passwordVisibility, setPasswordVisibility)
            )}
          </Grid>
        </Stack>
      </Paper>

      {tip ? (
        <Alert
          severity="success"
          icon={<CheckCircleRoundedIcon fontSize="inherit" />}
          sx={{ borderRadius: 3, backgroundColor: "#ecfdf5", color: "#166534" }}
        >
          {tip}
        </Alert>
      ) : null}
    </Stack>
  );
}

export function PreferencesStep({ form, onChange, errors, fields, note }) {
  return (
    <Stack spacing={2}>
      <Paper sx={{ ...sectionSx, p: { xs: 2, sm: 2.5 } }}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                bgcolor: "#ecfeff",
                color: "#0891b2",
              }}
            >
              <TuneRoundedIcon fontSize="small" />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 18, fontWeight: 800 }}>Default Settings</Typography>
              <Typography sx={{ fontSize: 13.5, color: "#64748b" }}>
                Set default preferences for this account.
              </Typography>
            </Box>
          </Stack>

          <Grid container spacing={2}>
            {fields.map((field) =>
              renderField(field, form, onChange, errors, {}, () => {})
            )}
          </Grid>
        </Stack>
      </Paper>

      {note ? (
        <Alert
          severity="success"
          icon={<CheckCircleRoundedIcon fontSize="inherit" />}
          sx={{ borderRadius: 3, backgroundColor: "#ecfdf5", color: "#166534" }}
        >
          {note}
        </Alert>
      ) : null}
    </Stack>
  );
}

export function PermissionToggleCard({
  title,
  description,
  items,
  values,
  parentAllowed,
  onToggle,
}) {
  return (
    <Paper sx={{ ...sectionSx, p: 2.25, height: "100%" }}>
      <Stack spacing={1.5}>
        <Box>
          <Typography sx={{ fontSize: 17, fontWeight: 800 }}>{title}</Typography>
          {description ? (
            <Typography sx={{ mt: 0.35, fontSize: 13.25, color: "#64748b" }}>{description}</Typography>
          ) : null}
        </Box>

        <Stack spacing={1.1}>
          {items.map((item) => {
            const allowed = parentAllowed?.[item.groupKey]?.[item.key] === true;
            const checked = Boolean(values?.[item.groupKey]?.[item.key]);
            return (
              <Paper
                key={`${item.groupKey}.${item.key}`}
                variant="outlined"
                sx={{
                  p: 1.3,
                  borderRadius: 3,
                  borderColor: allowed ? "#dbeafe" : "#e5e7eb",
                  backgroundColor: allowed ? "#f8fbff" : "#f8fafc",
                }}
              >
                <Stack direction="row" justifyContent="space-between" spacing={1.5} alignItems="center">
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: allowed ? "#0f172a" : "#94a3b8",
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: allowed ? "#64748b" : "#94a3b8" }}>
                      {allowed ? item.helperText || "Access available for this user." : "Locked by parent permission."}
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={0.75} alignItems="center">
                    {!allowed ? <LockOutlinedIcon sx={{ color: "#94a3b8", fontSize: 17 }} /> : null}
                    <Switch
                      checked={allowed ? checked : false}
                      onChange={() => onToggle(item.groupKey, item.key)}
                      disabled={!allowed}
                    />
                  </Stack>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      </Stack>
    </Paper>
  );
}

export function AttributeCardSelector({
  options,
  attributeDefinitions = options,
  disabledKeys = [],
  selectedKey,
  onSelectKey,
  draftValue,
  onDraftValueChange,
  onAdd,
  attributes,
  onUpdateAttribute,
  onRemoveAttribute,
  title = "Extra Information (Attributes)",
  description = "Select a field, enter a value, then add it to the account.",
}) {
  const selectedOption = useMemo(
    () => attributeDefinitions.find((option) => option.key === selectedKey) || null,
    [attributeDefinitions, selectedKey]
  );

  return (
    <Stack spacing={2}>
      <Paper sx={{ ...sectionSx, p: { xs: 2, sm: 2.5 } }}>
        <Stack spacing={2}>
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 800 }}>{title}</Typography>
            <Typography sx={{ mt: 0.45, color: "#64748b", fontSize: 13.5 }}>{description}</Typography>
          </Box>

          <Grid container spacing={1.5}>
            {options.map((option) => {
              const active = selectedKey === option.key;
              const disabled = disabledKeys.includes(option.key);
              return (
                <Grid item xs={6} sm={4} md={3} key={option.key}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => onSelectKey(option.key)}
                    disabled={disabled}
                    sx={{
                      justifyContent: "flex-start",
                      textTransform: "none",
                      borderRadius: 3,
                      p: 1.35,
                      minHeight: 74,
                      borderColor: active ? "#8b5cf6" : "#e5e7eb",
                      backgroundColor: active ? "#faf5ff" : "#fff",
                      color: "#0f172a",
                    }}
                  >
                    <Stack alignItems="flex-start" spacing={0.4}>
                      <Typography sx={{ fontWeight: 800, fontSize: 13.5 }}>{option.label}</Typography>
                      <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                        {option.type === "checkbox" ? "Yes / No" : option.type}
                      </Typography>
                    </Stack>
                  </Button>
                </Grid>
              );
            })}
          </Grid>

          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 3,
              borderColor: "#e5e7eb",
              backgroundColor: "#fbfdff",
            }}
          >
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "flex-end" }}>
              <FormControl fullWidth size="medium">
                <InputLabel>Selected Field</InputLabel>
                <Select
                  value={selectedKey}
                  label="Selected Field"
                  onChange={(event) => onSelectKey(event.target.value)}
                >
                  {options.map((option) => (
                    <MenuItem key={option.key} value={option.key}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedOption ? (
                selectedOption.type === "select" ? (
                  <TextField
                    select
                    fullWidth
                    label="Enter Value"
                    value={draftValue}
                    onChange={(event) => onDraftValueChange(event.target.value)}
                  >
                    {(selectedOption.options || []).map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : selectedOption.type === "checkbox" ? (
                  <Paper
                    variant="outlined"
                    sx={{ px: 1.25, py: 1, borderRadius: 3, minWidth: { md: 220 } }}
                  >
                    <FormControlLabel
                      sx={{ m: 0 }}
                      control={
                        <Switch
                          checked={Boolean(draftValue)}
                          onChange={(event) => onDraftValueChange(event.target.checked)}
                        />
                      }
                      label="Enable this field"
                    />
                  </Paper>
                ) : (
                  <TextField
                    fullWidth
                    label="Enter Value"
                    type={selectedOption.type || "text"}
                    value={draftValue}
                    onChange={(event) => onDraftValueChange(event.target.value)}
                  />
                )
              ) : (
                <TextField fullWidth disabled label="Enter Value" value="" />
              )}

              <Button
                variant="contained"
                onClick={onAdd}
                startIcon={<AddRoundedIcon />}
                disabled={!selectedOption}
                sx={{
                  borderRadius: 3,
                  px: 2.25,
                  py: 1.15,
                  minWidth: 132,
                  background: "linear-gradient(135deg, #7c3aed, #6d28d9)",
                }}
              >
                Add Field
              </Button>
            </Stack>
          </Paper>
        </Stack>
      </Paper>

      <Paper sx={{ ...sectionSx, p: { xs: 2, sm: 2.25 } }}>
        <Stack spacing={1.5}>
          <Typography sx={{ fontSize: 17, fontWeight: 800 }}>
            Added Fields ({attributes.length})
          </Typography>

          {attributes.length ? (
            <Stack spacing={1.1}>
              {attributes.map((attribute, index) => {
                const config = attributeDefinitions.find((option) => option.key === attribute.key) || attribute;
                return (
                  <Paper
                    key={`${attribute.key}-${index}`}
                    variant="outlined"
                    sx={{
                      p: 1.35,
                      borderRadius: 3,
                      borderColor: "#e5e7eb",
                      backgroundColor: "#fff",
                    }}
                  >
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1.25}
                      justifyContent="space-between"
                      alignItems={{ sm: "center" }}
                    >
                      <Box sx={{ minWidth: 160 }}>
                        <Typography sx={{ fontWeight: 800 }}>{config.label || attribute.label || attribute.key}</Typography>
                        <Typography sx={{ fontSize: 12.5, color: "#64748b" }}>{config.key}</Typography>
                      </Box>

                      <Box sx={{ flex: 1 }}>
                        {config.type === "select" ? (
                          <TextField
                            select
                            fullWidth
                            value={attribute.value || ""}
                            onChange={(event) => onUpdateAttribute(index, event.target.value)}
                          >
                            {(config.options || []).map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </TextField>
                        ) : config.type === "checkbox" ? (
                          <FormControlLabel
                            sx={{ m: 0 }}
                            control={
                              <Switch
                                checked={Boolean(attribute.value)}
                                onChange={(event) => onUpdateAttribute(index, event.target.checked)}
                              />
                            }
                            label="Enabled"
                          />
                        ) : (
                          <TextField
                            fullWidth
                            type={config.type || "text"}
                            value={attribute.value || ""}
                            onChange={(event) => onUpdateAttribute(index, event.target.value)}
                          />
                        )}
                      </Box>

                      <Stack direction="row" spacing={0.75}>
                        <IconButton size="small" sx={{ color: "#2563eb" }}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" sx={{ color: "#dc2626" }} onClick={() => onRemoveAttribute(index)}>
                          <DeleteOutlineRoundedIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          ) : (
            <Typography sx={{ color: "#64748b", fontSize: 14 }}>
              No extra fields added yet.
            </Typography>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}
